import { useFacilityContext } from '@aspendental/shared-components-web';
import { useMediaQuery, useTheme } from '@mui/material';
import { useRouter } from 'next/router';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { getServerSideTranslations } from '../utils/get-serverside-translations';
import FindLocation from '@src/components/FindLocation/FindLocation';
import ReasonForVisit from '@src/components/ReasonForVisit/ReasonForVisit';
import Stepper from '@src/components/Stepper/Stepper';
import TabbedForm from '@src/components/TabbedForm';
import { APP_CONFIGURATIONS, MOBILE_STEPPER_ACTIVE, SCHEDULER_BASE_URL, TagTheme } from '@src/constants';
import { useReasonForVisitFromUrlAnalytics } from '@src/hooks/useReasonForVisitFromUrlAnalytics';
import { Application, DigitalVisitReasonEnum } from '@src/lib/__generated/api';
import { fetchAppData } from '@src/pages/utils/fetch-app-data';
import getConfigByBrand from '@src/pages/utils/get-config-by-brand';
import { useGetFacility } from '@src/services/useGetFacility';
import { useGetFacilityLocations } from '@src/services/useGetFacilityLocations';
import { useAppStore, useAppointmentStore, useFormSectionStore, usePatientInformationFormStore, useReasonForVisitStore } from '@src/store';
import useNeedsBasedQuestionsStore from '@src/store/modules/needs-based-questions.store';
const visitReasonPaths: { [key in DigitalVisitReasonEnum]: string[] } = require('@src/visit-reason-paths');
const useIsomorphicLayoutEffect = globalThis.window === undefined ? useEffect : useLayoutEffect;

/**
 * V2 scheduling page for A/B testing.
 *
 * Mirrors /schedule-an-appointment but uses the consolidated Patient Info tab
 * (ConsolidatedPatientInformationForm) which merges the Patient Information and
 * Additional Patient Information steps into a single tab.
 *
 * Route constant: SCHEDULER_V2_BASE_URL — update that value in src/constants.ts
 * to rename the route without touching this file.
 */
const HomeV2 = ({
  shouldUseMottoConfig = false,
  resultURL,
  graphURL,
  userEventsURL,
  reasonForVisitFromUrl,
  successCardsCollection,
  marketingContent,
  isMottoFromConfig,
  googleMapApiKey,
  eapiBaseUrl,
  experianApiKey,
  previsitPaperworkUrl
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    hasReasonForVisit,
    setReasonForVisit,
    setHasReasonForVisit,
    reasonForVisit
  } = useReasonForVisitStore(store => store);
  const {
    initializeFormNodes,
    completeActiveNode,
    isInitialized
  } = useFormSectionStore(store => store);
  const geoLocation = usePatientInformationFormStore(store => store.geoLocation);
  const setGeolocation = usePatientInformationFormStore(store => store.setGeolocation);
  const {
    latitude,
    longitude
  } = geoLocation;
  const facilityDetails = useFacilityContext();
  const setRestUrl = useAppStore(store => store.setRestUrl);
  const setGraphUrl = useAppStore(store => store.setGraphUrl);
  // V2 flow is never mottoFlow or denturesFlow
  const setMottoFlow = useAppStore(store => store.setMottoFlow);
  const setDenturesFlow = useAppStore(store => store.setDenturesFlow);
  const setUserEventsUrl = useAppStore(store => store.setUserEventsUrl);
  const setGoogleMapApiKey = useAppStore(store => store.setGoogleMapApiKey);
  const setEapiBaseUrl = useAppStore(store => store.setEapiBaseUrl);
  const setPrevisitPaperworkUrl = useAppStore(store => store.setPrevisitPaperworkUrl);
  const setAppointmentSlot = useAppointmentStore(store => store.setAppointmentSlot);
  const setAppointmentFacility = useAppointmentStore(store => store.setAppointmentFacility);
  useEffect(() => {
    setRestUrl(resultURL);
    setGraphUrl(graphURL);
    setUserEventsUrl(userEventsURL);
    setGoogleMapApiKey(googleMapApiKey);
    setEapiBaseUrl(eapiBaseUrl);
    setPrevisitPaperworkUrl(previsitPaperworkUrl);
  }, [resultURL, graphURL, userEventsURL, googleMapApiKey, eapiBaseUrl, previsitPaperworkUrl, experianApiKey, setRestUrl, setGraphUrl, setUserEventsUrl, setGoogleMapApiKey, setEapiBaseUrl, setPrevisitPaperworkUrl]);
  const setHasNeedsBasedQuestions = useNeedsBasedQuestionsStore(store => store.setHasNeedsBasedQuestions);
  const router = useRouter();
  const facilityCodeFromURL = router.isReady ? new URLSearchParams(router.asPath.split('?')[1]).get('fc') : null;
  const {
    data: facility
  } = useGetFacility({
    facilityCode: facilityCodeFromURL ?? facilityDetails?.facilityCode
  });
  const [sessionDataReady, setSessionDataReady] = useState(false);
  useEffect(() => {
    setHasNeedsBasedQuestions(!!facility?.needsBasedQuestions);
    if (facility?.latitude && facility.longitude) {
      setGeolocation(facility.latitude, facility.longitude);
    }
  }, [facility, setGeolocation, setHasNeedsBasedQuestions]);
  const facilityParams = useMemo(() => {
    if (!facility) return null;
    return {
      reasonForVisitFromUrl,
      longitude: facility.longitude,
      latitude: facility.latitude,
      application: Application.AspenDental
    };
  }, [facility]);
  const paramsForHook = useMemo(() => {
    return facilityParams ?? {};
  }, [facilityParams]);
  const {
    data: facilityLocations
  } = useGetFacilityLocations(paramsForHook, {
    enabled: !!facilityParams && sessionDataReady
  });
  const isMottoFlow = reasonForVisitFromUrl === DigitalVisitReasonEnum.ClearAligners;
  const isDenturesFlow = reasonForVisitFromUrl === DigitalVisitReasonEnum.Dentures || reasonForVisit === DigitalVisitReasonEnum.Dentures;
  useEffect(() => {
    if (reasonForVisitFromUrl) {
      setReasonForVisit(reasonForVisitFromUrl);
      setMottoFlow(isMottoFlow);
      setHasReasonForVisit(true);
    }
    setMottoFlow(isMottoFlow);
    setDenturesFlow(isDenturesFlow);
  }, [reasonForVisitFromUrl, isDenturesFlow, isMottoFlow, isMottoFromConfig, setReasonForVisit, setHasReasonForVisit, setMottoFlow, setDenturesFlow]);
  useReasonForVisitFromUrlAnalytics({
    reasonForVisitFromUrl: reasonForVisitFromUrl,
    isMottoFlow,
    facilityCode: facilityDetails?.officeInfo?.code,
    facilityName: facilityDetails?.officeInfo?.name
  });

  // Motto/ClearAligners and Dentures use MOTTO_FORM_NODES (Date & Time first); all other V2 reasons use V2_FORM_NODES.
  // Use a layout effect in the browser so this client-side reorder commits before React paints the hydrated update.
  useIsomorphicLayoutEffect(() => {
    initializeFormNodes(isMottoFlow ? reasonForVisitFromUrl : isDenturesFlow ? 'Dentures' : 'V2');
  }, [initializeFormNodes, isDenturesFlow, isMottoFlow, reasonForVisitFromUrl]);
  useEffect(() => {
    const sessionSlotData = sessionStorage.getItem('selectedSlotTimeId');
    if (sessionSlotData && isInitialized && reasonForVisitFromUrl) {
      const slotData = JSON.parse(sessionSlotData);
      setAppointmentSlot(slotData);
      completeActiveNode();
      setSessionDataReady(true);
      sessionStorage.removeItem('selectedSlotTimeId');
    }
  }, [isInitialized, reasonForVisitFromUrl]);
  useEffect(() => {
    if (facilityLocations?.length) {
      setAppointmentFacility(facilityLocations[0]);
    }
  }, [facilityLocations]);
  if (reasonForVisitFromUrl && latitude + longitude === 0 && !(facilityCodeFromURL ?? facilityDetails?.facilityCode)) {
    return <FindLocation />;
  }
  if (hasReasonForVisit || reasonForVisitFromUrl) {
    if (isSmallScreen && MOBILE_STEPPER_ACTIVE) {
      return <Stepper shouldUseMottoConfig={shouldUseMottoConfig} specialFlow={isDenturesFlow || isMottoFlow} v2Flow={!isDenturesFlow && !isMottoFlow} successCardsCollection={successCardsCollection} marketingContent={marketingContent} />;
    } else {
      return <TabbedForm shouldUseMottoConfig={shouldUseMottoConfig} specialFlow={isDenturesFlow || isMottoFlow} v2Flow={!isDenturesFlow && !isMottoFlow} successCardsCollection={successCardsCollection} marketingContent={marketingContent} />;
    }
  }
  return <ReasonForVisit webFacilityDetails={facilityDetails} data-sentry-element="ReasonForVisit" data-sentry-component="HomeV2" data-sentry-source-file="index.page.tsx" />;
};
const removeUrlQueryParams = (url: string) => {
  const queryIndex = url.indexOf('?');
  if (queryIndex < 0) {
    return url;
  }
  return url.substring(0, queryIndex);
};
const parseReasonForVisitFromUrl = (url: string): DigitalVisitReasonEnum | null => {
  if (url === '/') return null;
  const urlRoute = removeUrlQueryParams(url);
  const currentReasonForVisit = urlRoute.replaceAll(SCHEDULER_BASE_URL, '').replaceAll('/', '');
  for (const [key, path] of Object.entries(visitReasonPaths)) {
    if (path.includes(currentReasonForVisit)) {
      return key as DigitalVisitReasonEnum;
    }
  }
  return null;
};
export const getServerSideProps = async ({
  req,
  locale
}) => {
  const headerTagTheme = Array.isArray(req.headers['x-tag-theme']) ? (req.headers['x-tag-theme'] as TagThemeType[])[0] : req.headers['x-tag-theme'] as TagThemeType;
  const stateCodeFromHeaders = req.headers['cloudfront-viewer-country-region'] ?? '';
  const tagTheme: TagThemeType = headerTagTheme || process.env.TAG_BRAND_THEME || 'aspendental';
  const initialConfig = getConfigByBrand(tagTheme);
  const hostname = req.headers.host || '';
  const isMottoFromDomain = hostname.includes('mottoclearaligners.com');
  const isMottoFromConfig = tagTheme === 'motto';
  const shouldUseMottoConfig = isMottoFromDomain || isMottoFromConfig;
  const brandConfig = shouldUseMottoConfig ? getConfigByBrand(TagTheme.MottoAligners) : initialConfig;
  const fetchedData = await fetchAppData({
    appName: brandConfig.contentfulAppId,
    confirmationStepId: brandConfig.confirmationStepId,
    path: req.url,
    locale
  });
  const reasonForVisitFromUrl = parseReasonForVisitFromUrl(req.url);
  const isMottoPath = reasonForVisitFromUrl === DigitalVisitReasonEnum.ClearAligners;

  // FLOW: Use motto flow if motto branding is active OR path is /mottoaligners
  const isMottoFlow = shouldUseMottoConfig || isMottoPath;
  const environment = process.env.ENVIRONMENT_NAME;
  const {
    services
  } = APP_CONFIGURATIONS;
  const resultURL = environment === 'prod' ? services.prod.EAPI_REST_URL : services.nonprod.EAPI_REST_URL;
  const graphURL = environment === 'prod' ? services.prod.EAPI_GRAPHQL_URL : services.nonprod.EAPI_GRAPHQL_URL;
  const userEventsURL = environment === 'prod' ? services.prod.USER_EVENTS_URL : services.nonprod.USER_EVENTS_URL;
  const formEventsURL = environment === 'prod' ? services.prod.FORM_EVENTS_URL : services.nonprod.FORM_EVENTS_URL;
  const googleMapApiKey = environment === 'prod' ? brandConfig.services.prod.GOOGLE_MAP_API_KEY : brandConfig.services.nonprod.GOOGLE_MAP_API_KEY;
  const eapiBaseUrl = environment === 'prod' ? services.prod.EAPI_BASE_URL : services.nonprod.EAPI_BASE_URL;
  const googleURL = environment === 'prod' ? services.prod.GOOGLE_RESERVE_API : services.nonprod.GOOGLE_RESERVE_API;
  const previsitPaperworkUrl = environment === 'prod' ? services.prod.PREVISIT_PAPERWORK_URL : services.nonprod.PREVISIT_PAPERWORK_URL;
  const pageReferrer = req.headers.referer || '';
  let hasIpBasedCoordinates = false;
  let coordinates = {
    latitude: 0,
    longitude: 0
  };
  if (req.headers['cloudfront-viewer-latitude'] && req.headers['cloudfront-viewer-longitude']) {
    hasIpBasedCoordinates = true;
    coordinates = {
      latitude: Number.parseFloat(req.headers['cloudfront-viewer-latitude'] as string),
      longitude: Number.parseFloat(req.headers['cloudfront-viewer-longitude'] as string)
    };
  }
  return {
    props: {
      resultURL,
      graphURL,
      userEventsURL,
      formEventsURL,
      eapiBaseUrl,
      googleURL,
      reasonForVisitFromUrl,
      googleMapApiKey,
      previsitPaperworkUrl,
      isMottoFromConfig,
      config: brandConfig,
      shouldUseMottoConfig,
      mottoFlow: isMottoFlow,
      user: {
        coordinates,
        hasIpBasedCoordinates
      },
      ...(await getServerSideTranslations(locale)),
      env: {
        ENVIRONMENT_NAME: process.env.ENVIRONMENT_NAME
      },
      pageReferrer,
      ...fetchedData,
      stateCodeFromHeaders
    }
  };
};
export default HomeV2;